import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Shield, Bell, Trash2, ChevronRight, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import DeleteAccountDialog from '@/components/DeleteAccountDialog';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const AccountSettingsPage = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const settingsSections = [
    {
      id: 'profile',
      icon: User,
      label: 'Profile Information',
      description: 'Name, email, and personal details',
      action: () => navigate('/medical'),
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      id: 'security',
      icon: Shield,
      label: 'Security & Privacy',
      description: 'Password, 2FA, and login settings',
      action: () => navigate('/admin/security'),
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-50',
    },
    {
      id: 'notifications',
      icon: Bell,
      label: 'Notifications',
      description: 'Reminders and alert preferences',
      action: () => navigate('/settings/notifications'),
      color: 'text-amber-500',
      bgColor: 'bg-amber-50',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="h-9 w-9 rounded-full hover:bg-slate-100"
              data-testid="back-button"
            >
              <ArrowLeft className="h-5 w-5 text-slate-600" />
            </Button>
            <h1 className="text-lg font-semibold text-slate-800">Account Settings</h1>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* User Info Card */}
        <Card className="overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                <User className="h-7 w-7 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-800">{user?.name || 'User'}</p>
                <p className="text-sm text-slate-500">{user?.email || 'No email'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Settings Sections */}
        <div className="space-y-2">
          {settingsSections.map((section) => (
            <Card
              key={section.id}
              className="cursor-pointer hover:shadow-md transition-all active:scale-[0.99]"
              onClick={section.action}
              data-testid={`settings-${section.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className={`w-11 h-11 rounded-xl ${section.bgColor} flex items-center justify-center`}>
                    <section.icon className={`h-5 w-5 ${section.color}`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-800">{section.label}</p>
                    <p className="text-sm text-slate-500">{section.description}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Actions */}
        <div className="pt-4 space-y-3">
          {/* Logout Button */}
          <Button
            variant="outline"
            className="w-full justify-start gap-3 h-12 border-slate-200 text-slate-700 hover:bg-slate-50"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-5 w-5" />
            Log Out
          </Button>

          {/* Delete Account Button */}
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 h-12 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            onClick={() => setShowDeleteDialog(true)}
            data-testid="delete-account-button"
          >
            <Trash2 className="h-5 w-5" />
            Delete Account
          </Button>

          {/* Warning text */}
          <p className="text-xs text-center text-slate-400 pt-2">
            Deleting your account will permanently remove all your data.
            {'\n'}Store subscriptions must be cancelled separately in App Store/Play Store.
          </p>
        </div>
      </main>

      {/* Delete Account Dialog */}
      <DeleteAccountDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
      />
    </div>
  );
};

export default AccountSettingsPage;
